import { useCallback, useEffect, useState } from 'react';
import { Button, Grid, MenuItem, TextField, Typography } from '@material-ui/core';
import { InfoCard, Link, WarningPanel } from '@backstage/core-components';
import { useReleasesCall } from './useReleases';

type Deploy = { tag: string; date: string; url: string };
type State = { allowed: boolean; environments?: Record<string, { current: Deploy; previous: Deploy[] }>; runsUrl?: string };

const ORDER = ['dev', 'qa', 'prod'];
// Lo que conviene saber antes de confirmar, por ambiente
const NOTE: Record<string, string> = {
  prod: ' Prod espera la aprobación de devops.',
  qa: ' qa quedará con una versión anterior a la release en curso: vuelve a desplegar la release en qa antes de pasarla a main (la regresión y build-once se basan en lo que hay en qa).',
};
const when = (d: string) => new Date(d).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' });

/** Volver a una versión anterior: relanza el CD con ese tag (aprobación y freeze incluidos). Solo devops y arquitectos. */
export const RollbackPanel = ({ slug }: { slug: string }) => {
  const releases = useReleasesCall();
  const name = slug.split('/')[1] ?? slug;
  const [state, setState] = useState<State>();
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();

  const call = useCallback(
    (body?: unknown) => (body ? releases('/rollback', body) : releases(`/rollback?slug=${encodeURIComponent(slug)}`)),
    [releases, slug],
  );

  useEffect(() => {
    setState(undefined);
    setChoice({});
    setDone(undefined);
    setError(undefined);
    if (slug.startsWith('deuna-platform/')) call().then(setState, e => setError(String(e?.message ?? e)));
  }, [call, slug]);

  if (!state?.allowed) return error ? <WarningPanel title="No se pudo leer las versiones para rollback" message={error} /> : null;

  const rollback = async (env: string, from: string, tag: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Volver ${name} en ${env} de ${from} a ${tag}?${NOTE[env] ?? ''}`)) return;
    setBusy(true);
    setError(undefined);
    try {
      await call({ slug, tag, reason });
      setDone(`Rollback a ${tag} lanzado en ${env}.`);
      setReason('');
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const envs = ORDER.filter(env => state.environments?.[env]);
  return (
    <InfoCard title={`Rollback · ${name}`} subheader="Relanza el CD con una versión que ya se desplegó en ese ambiente; pasa por la aprobación y el freeze">
      {error && <WarningPanel title="Algo falló" message={error} />}
      {done && (
        <Typography style={{ marginBottom: 8 }}>
          {done} <Link to={state.runsUrl ?? '#'}>Ver la corrida del CD</Link>
        </Typography>
      )}
      {envs.length === 0 && <Typography>Sin despliegues exitosos del CD en este repo.</Typography>}
      <Grid container spacing={2} alignItems="center">
        {envs.map(env => {
          const { current, previous } = state.environments![env];
          return (
            <Grid item xs={12} key={env} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <Typography style={{ minWidth: 220 }}>
                <b>{env}</b>: {current.tag} <span style={{ opacity: 0.7 }}>({when(current.date)})</span>
              </Typography>
              <TextField
                select
                size="small"
                label="Volver a"
                style={{ minWidth: 260 }}
                value={choice[env] ?? ''}
                onChange={e => setChoice({ ...choice, [env]: e.target.value as string })}
                disabled={previous.length === 0}
              >
                {previous.map(d => (
                  <MenuItem key={d.tag} value={d.tag}>
                    {d.tag} · {when(d.date)}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                color="secondary"
                disabled={busy || !choice[env] || !reason.trim()}
                onClick={() => rollback(env, current.tag, choice[env])}
              >
                Hacer rollback
              </Button>
            </Grid>
          );
        })}
        {envs.length > 0 && (
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Motivo (obligatorio)" value={reason} onChange={e => setReason(e.target.value)} inputProps={{ maxLength: 300 }} />
          </Grid>
        )}
      </Grid>
    </InfoCard>
  );
};
